import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { forgotPassword } from "@/lib/api";
import { Compass as CompassIcon, Loader2, Eye, EyeOff, ArrowLeft, MailCheck } from "lucide-react";

const inputCls =
  "w-full border border-stone-300 rounded-sm px-3 py-2.5 text-[14px] focus:border-[#8C3A2A] outline-none transition-colors";

// Reusable password field with a show/hide toggle.
function PasswordField({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        required
        minLength={10}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className={inputCls + " pr-10"}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${testId}-toggle`}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-[#8C3A2A] transition-colors"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

// Sign-in / create-account / forgot-password modal. `reason` optionally explains
// why we're prompting. onSuccess(user) fires after auth succeeds.
export default function AuthModal({ open, onOpenChange, onSuccess, reason }) {
  const { login, register } = useAuth();
  const [view, setView] = useState("login"); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail(""); setPassword(""); setName(""); setError(""); setBusy(false);
      setForgotSent(false); setView("login");
    }
  }, [open]);

  const submitAuth = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = view === "login"
      ? await login(email.trim(), password)
      : await register(email.trim(), password, name.trim());
    setBusy(false);
    if (res.ok) {
      onOpenChange(false);
      onSuccess?.(res.user);
    } else {
      setError(res.error);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send the reset email. Please try again.");
    }
    setBusy(false);
  };

  const tabBtn = (v, label, testId) => (
    <button
      type="button"
      data-testid={testId}
      onClick={() => { setView(v); setError(""); }}
      className={`flex-1 text-[12.5px] py-1.5 rounded-sm transition-colors ${view === v ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[#8C3A2A] mb-1">
            <CompassIcon className="h-5 w-5" />
            <span className="font-mono-panel text-[11px] uppercase tracking-[0.18em]">The Writing Studio account</span>
          </div>
          <DialogTitle className="font-serif-display text-2xl text-stone-900">
            {view === "login" ? "Welcome back" : view === "register" ? "Create your account" : "Reset your password"}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-stone-500">
            {view === "forgot"
              ? "Enter your email and we'll send you a link to set a new password."
              : reason || "Save your paragraphs and reviews, and pick up where you left off."}
          </DialogDescription>
        </DialogHeader>

        {view === "forgot" ? (
          forgotSent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100">
                <MailCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-[14px] text-stone-700 mb-1">Check your inbox</p>
              <p className="text-[13px] text-stone-500 mb-5">
                If an account exists for <span className="text-stone-700">{email}</span>, a reset link is on its way (check spam too).
              </p>
              <button
                type="button"
                data-testid="forgot-back-to-login"
                onClick={() => { setView("login"); setForgotSent(false); }}
                className="inline-flex items-center gap-1.5 text-[12.5px] text-[#8C3A2A] hover:underline"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1 font-mono-panel">Email</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="forgot-email-input" className={inputCls}
                />
              </div>
              {error && (
                <p data-testid="auth-error" className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{error}</p>
              )}
              <button
                type="submit" disabled={busy}
                data-testid="forgot-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[13.5px] py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
              </button>
              <button
                type="button"
                data-testid="forgot-cancel"
                onClick={() => { setView("login"); setError(""); }}
                className="w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </form>
          )
        ) : (
          <>
            <div className="flex gap-1 bg-stone-100 rounded-sm p-1 mb-1" data-testid="auth-tabs">
              {tabBtn("login", "Sign in", "auth-tab-login")}
              {tabBtn("register", "Create account", "auth-tab-register")}
            </div>

            <form onSubmit={submitAuth} className="space-y-3">
              {view === "register" && (
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1 font-mono-panel">Name</label>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your name" data-testid="auth-name-input" className={inputCls}
                  />
                </div>
              )}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1 font-mono-panel">Email</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" data-testid="auth-email-input" className={inputCls}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] uppercase tracking-[0.14em] text-stone-400 font-mono-panel">Password</label>
                  {view === "login" && (
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      onClick={() => { setView("forgot"); setError(""); }}
                      className="text-[11.5px] text-[#8C3A2A] hover:underline"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <PasswordField
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={view === "register" ? "At least 10 characters" : "Your password"}
                  testId="auth-password-input"
                />
              </div>

              {error && (
                <p data-testid="auth-error" className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{error}</p>
              )}

              <button
                type="submit" disabled={busy}
                data-testid="auth-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[13.5px] py-2.5 rounded-sm hover:bg-[#6B2C20] transition-colors disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {view === "login" ? "Sign in" : "Create account"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
